import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Setup() {
    const { user, setup, loading } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [needSetup, setNeedSetup] = useState(true);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        api.get("/auth/setup-status")
            .then((r) => setNeedSetup(r.data.setup_required))
            .finally(() => setChecking(false));
    }, []);

    if (loading || checking) {
        return (
            <div className="flex h-screen items-center justify-center bg-background">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    if (user) return <Navigate to="/" replace />;
    if (!needSetup) return <Navigate to="/login" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await setup({ name, email, password });
            toast.success("Admin account created");
            navigate("/");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center sdc-soft-bg px-6 py-12">
            <div className="w-full max-w-md">
                <div className="mb-8 flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                        <span className="font-display text-lg font-extrabold">S</span>
                    </div>
                    <div>
                        <div className="font-display text-2xl font-extrabold tracking-tight">
                            SDC
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            First-time Setup
                        </div>
                    </div>
                </div>

                <div className="sdc-card p-8">
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        You'll be the admin
                    </div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">
                        Create your admin account.
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        This is a one-time step. After this, you can add more users and assign tasks.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="setup-form">
                        <div className="space-y-2">
                            <Label htmlFor="name">Full name</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Jane Sharma"
                                required
                                data-testid="setup-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="jane@firm.com"
                                required
                                data-testid="setup-email-input"
                            />
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    required
                                    data-testid="setup-password-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm">Confirm password</Label>
                                <Input
                                    id="confirm"
                                    type="password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    data-testid="setup-confirm-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-11"
                            disabled={submitting}
                            data-testid="setup-submit-button"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Create admin & start"
                            )}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
